// ---------------------------------------------------------------------------
// /campaigns/fallback — dashboard page (OPUS-D14)
// ---------------------------------------------------------------------------
// Route path: /campaigns/fallback
// Mirrors the api surface at /api/v1/campaign-fallback (feature #76).
//
// Layout: brand-gradient headline, supporting paragraph, then the
// `<FallbackForm />` card. The page is purpose-built for the single
// "configure a fallback" task — listing across campaigns is a separate
// future page.
// ---------------------------------------------------------------------------

import type { RootRoute } from "@tanstack/react-router";
import { createRoute } from "@tanstack/react-router";

import { FallbackForm } from "@/features/fallback/FallbackForm";

function CampaignsFallbackPage() {
	return (
		<main className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-12">
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
					Campaigns
				</p>
				<h1 className="text-brand-gradient text-4xl font-semibold tracking-tight sm:text-5xl">
					Campaign fallback
				</h1>
				<p className="max-w-2xl text-sm leading-relaxed text-zinc-500">
					Redirect clicks that get blocked by geo, device, or OS targeting
					to a backup destination of your choosing. Pick the failure reason,
					paste an https URL, and save. Disable the toggle to remove the row
					for that reason.
				</p>
			</header>

			<FallbackForm />

			<section className="rounded-[8px] border border-zinc-200 bg-zinc-50/60 p-5 text-xs leading-relaxed text-zinc-600">
				<h2 className="mb-2 text-sm font-semibold text-zinc-800">
					How fallback rows resolve
				</h2>
				<ul className="ml-4 list-disc space-y-1">
					<li>
						Reason-specific rows win. If a click is blocked by geo and a{" "}
						<span className="font-semibold">Geo blocked</span> row exists, that
						URL is used.
					</li>
					<li>
						The{" "}
						<span className="font-semibold">Any blocked click</span> row is the
						catch-all; it fires when no reason-specific row matches.
					</li>
					<li>URLs must be absolute and use the https scheme.</li>
				</ul>
			</section>
		</main>
	);
}

// ---- tanstack-router route export -----------------------------------------
// The dash scaffold (opD01) wires routes programmatically. Root (not this
// agent) will add one line to `src/router.tsx` to attach this route:
//
//   import { createCampaignsFallbackRoute } from "./routes/campaigns.fallback";
//   const campaignsFallbackRoute = createCampaignsFallbackRoute(rootRoute);
//   rootRoute.addChildren([..., campaignsFallbackRoute]);

// biome-ignore lint/suspicious/noExplicitAny: RootRoute generics are noisy
// and the scaffold consumer only needs "pass the parent" ergonomics.
export function createCampaignsFallbackRoute(
	parent: RootRoute<any, any, any, any, any, any, any, any, any>,
) {
	return createRoute({
		getParentRoute: () => parent,
		path: "/campaigns/fallback",
		component: CampaignsFallbackPage,
	});
}

export { CampaignsFallbackPage };
export default CampaignsFallbackPage;
